import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { MdCheckCircle } from "react-icons/md";
import { useSearchParams } from "react-router-dom";
import { DAYS, TASK_NOTES_PLACEHOLDER_BY_TYPE } from "./constants";
import { Machine, Task, TaskStatus, MACHINE_TYPES } from "./types";
import { useActionOverlayStore } from "./state/actionOverlay";
import { assertBillingWriteAllowed, PaymentRequiredError } from "../utils";
import { useUserStore } from "../user/state/store";
import { useSuperuserContextStore } from "./state/superuserContext";
import { useToastStore, type ToastPayload } from "./state/toast";
import {
  formatUserLabel,
  formatUserSearchText,
  type UserLabelInput,
} from "./userLabel";

export { formatUserLabel, formatUserSearchText };

export { parseNumericInput, formatNumericInputForSync } from "../utils";

export function to24h(hour12: number, minute: number, period: "AM" | "PM") {
  let h = hour12 % 12;
  if (period === "PM") h += 12;
  return `${h.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")}`;
}

export function from24h(time?: string) {
  if (!time) return { hour: 12, minute: 0, period: "AM" as const };
  const [hStr, mStr] = time.split(":");
  let h = Number(hStr);
  const minute = Number(mStr || 0);
  if (h === 24 && minute === 0) h = 0;
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return { hour: h, minute: minute || 0, period };
}

export function SuccessMessage({
  message,
}: {
  message?: string | null;
}): React.ReactElement | null {
  if (!message) return null;
  const Icon = MdCheckCircle as unknown as React.ElementType;
  return React.createElement(
    "div",
    { className: "flex items-center gap-2 text-emerald-600 text-sm" },
    React.createElement(Icon, { className: "h-4 w-4" }),
    React.createElement("span", null, message),
  );
}

type ServiceCadenceInput = {
  frequency?: string | number;
  custom?: string;
  days?: string[];
};

export function formatServiceCadenceSummary({
  frequency,
  custom,
  days = [],
}: ServiceCadenceInput): { primary: string; secondary: string } {
  const frequencyMap: Record<string, number> = {
    daily: 1,
    weekly: 7,
    bimonthly: 14,
    monthly: 30,
  };

  const parsedCustom = Number(custom);
  const parsedFrequency = Number(frequency);
  const numericFrequency = Number.isFinite(parsedFrequency) && parsedFrequency > 0
    ? parsedFrequency
    : undefined;
  const cadenceDays =
    numericFrequency ?? (frequency === "other"
      ? Number.isFinite(parsedCustom) && parsedCustom > 0
        ? parsedCustom
        : undefined
      : frequency
        ? frequencyMap[frequency]
        : undefined);

  const cadenceText = cadenceDays
    ? cadenceDays === 1
      ? "every day"
      : `every ${cadenceDays} days`
    : "once the cadence is specified";

  const normalizedDays = days.map((day) => {
    const unprefixed = day.replace(/^WeekDay\./i, "");
    return DAYS.find((candidate) => candidate.toLowerCase() === unprefixed.toLowerCase()) || unprefixed;
  });
  const sortedDays = sortDaysByWeek(normalizedDays);

  const joinDays = (list: string[]) =>
    list.length <= 1
      ? list[0] || ""
      : `${list.slice(0, -1).join(", ")} and ${list[list.length - 1]}`;

  const hasDays = sortedDays.length > 0;
  const dayPhrase = hasDays ? joinDays(sortedDays) : "";

  const usePreferred = frequency === "other";
  const daysClause =
    cadenceDays === 1
      ? "" // every day, no specific day clause
      : hasDays
        ? usePreferred
          ? ` with preferred days: ${dayPhrase}`
          : ` on ${dayPhrase}`
        : "";

  const primary = `This location will be scheduled for service ${cadenceText}${daysClause}.`;
  const secondary = "You can always update this later.";
  return { primary, secondary };
}

// Simple layout wrapper for filter rows to avoid re-defining flex utilities.
export function FilterBar({ children }: { children: ReactNode }) {
  return React.createElement(
    "div",
    { className: "flex flex-col md:flex-row md:items-center gap-3" },
    children,
  );
}

// Generic search helper: pass a list and the string selectors to match against.
export function filterBySearch<T>(
  items: T[],
  term: string,
  selectors: Array<(item: T) => string | undefined>,
) {
  if (!term) return items;
  const q = term.toLowerCase();
  return items.filter((item) =>
    selectors.some((fn) => (fn(item) || "").toLowerCase().includes(q)),
  );
}

export function buildSearchTerms(term: string): string[] {
  const base = term.toLowerCase().trim();
  if (!base) return [];
  const terms = new Set([base]);
  const match = /^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/.exec(base);
  if (match) {
    const month = Number(match[1]);
    const day = Number(match[2]);
    let year = Number(match[3]);
    if (Number.isNaN(month) || Number.isNaN(day) || Number.isNaN(year)) {
      return Array.from(terms);
    }
    if (year < 100) year += 2000;
    if (month < 1 || month > 12) return Array.from(terms);
    const date = new Date(year, month - 1, day);
    if (Number.isNaN(date.getTime())) return Array.from(terms);
    if (date.getMonth() !== month - 1 || date.getDate() !== day) {
      return Array.from(terms);
    }
    const iso = toLocalISODate(date);
    terms.add(iso.toLowerCase());
    terms.add(formatDate(iso).toLowerCase());
    terms.add(formatDateShort(iso).toLowerCase());
    terms.add(formatDateShortNoYear(iso).toLowerCase());
  }
  return Array.from(terms);
}

export function filterBySearchTerms<T>(
  items: T[],
  terms: string[],
  selectors: Array<(item: T) => string | undefined>,
) {
  if (!terms.length) return items;
  return items.filter((item) =>
    matchesSearchTerms(
      terms,
      selectors.map((selector) => selector(item)),
    ),
  );
}

export function matchesSearchTerms(
  terms: string[],
  values: Array<string | number | null | undefined>,
) {
  if (!terms.length) return true;
  return terms.some((term) => {
    const amount = searchAmount(term);
    return values.some((value) => {
      const text = String(value ?? "").toLowerCase();
      if (text.includes(term)) return true;
      if (amount === null) return false;
      if (searchAmount(text) === amount) return true;
      // Currency amounts may be embedded in labels such as "$4,920.00 fixed".
      const amounts = text.match(/[$€£¥]\s*\d[\d,]*(?:\.\d+)?/g) || [];
      return amounts.some((candidate) => searchAmount(candidate) === amount);
    });
  });
}

function searchAmount(value: string): string | null {
  const normalized = value.trim().replace(/^[$€£¥]\s*/, "");
  if (!/^(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d{1,2})?$/.test(normalized))
    return null;
  const [whole, fraction = ""] = normalized.replace(/,/g, "").split(".");
  return `${whole.replace(/^0+(?=\d)/, "")}.${fraction.padEnd(2, "0")}`;
}

export const TASK_STATUS_STYLES: Record<TaskStatus, string> = {
  pending: "bg-amber-100 text-amber-800 border border-amber-200",
  done: "bg-emerald-100 text-emerald-800 border border-emerald-200",
  unassigned: "bg-slate-100 text-slate-700 border border-slate-200",
  error: "bg-red-100 text-red-700 border border-red-200",
  cancelled: "bg-neutral-100 text-neutral-700 border border-neutral-200",
};

export const TASK_TYPE_LABELS: Record<string, string> = {
  default: "Default",
  machine_refill: "Refill",
  machine_clean: "Clean",
  machine_repair: "Repair",
  machine_refund: "Refund",
  machine_install: "Install",
  machine_remove: "Remove",
  machine_collection: "Collection",
  machine_service: "Service",
  machine_pickup_inventory: "Pickup Inventory",
  location_lead: "Growth Lead",
  other: "Other",
};

export function taskTypeLabel(type?: string) {
  if (!type) return "";
  return TASK_TYPE_LABELS[type] || type;
}

export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  pending: "Pending",
  done: "Done",
  unassigned: "Unassigned",
  error: "Error",
  cancelled: "Canceled",
};

export function taskStatusLabel(status?: TaskStatus | null) {
  if (!status) return "";
  return TASK_STATUS_LABELS[status] || status;
}

export function taskStatusTextClass(status?: string | null) {
  if (status === "done") return "text-emerald-700";
  if (status === "error") return "text-red-600";
  if (status === "cancelled") return "text-orange-600";
  if (status === "unassigned") return "text-blue-500";
  return "text-amber-700";
}

export async function withAction<T>(
  label: string,
  action: () => Promise<T>,
  options?: { suppressOverlay?: boolean },
): Promise<T> {
  const overlay = useActionOverlayStore.getState();
  if (!options?.suppressOverlay) {
    overlay.start(label);
  }
  try {
    return await action();
  } finally {
    if (!options?.suppressOverlay) {
      overlay.end();
    }
  }
}

/** Use the existing action overlay and top-bar feedback for form writes. */
export async function runSaveAction<T>(
  loading: string,
  success: string | ((result: T) => ToastPayload),
  action: () => Promise<T>,
): Promise<T> {
  try {
    const result = await withAction(loading, action);
    useToastStore.getState().show(typeof success === "string" ? { title: success } : success(result));
    return result;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    useToastStore.getState().show({
      title: "Unable to save changes",
      message: error instanceof Error ? error.message : "Please try again.",
      status: "error",
    });
    throw error;
  }
}

export async function withWriteAction<T>(
  label: string,
  action: () => Promise<T>,
  options?: { suppressOverlay?: boolean; allowPastDue?: boolean },
): Promise<T> {
  const superuserState = useSuperuserContextStore.getState();
  if (
    superuserState.enabled &&
    superuserState.selectedOrg &&
    !superuserState.writeEnabled
  ) {
    superuserState.requestWriteAttention();
    const message = "Superuser organization write access is not enabled.";
    useToastStore.getState().show({
      title: "Write Mode Required",
      message: "Enable write mode before saving changes.",
      status: "error",
    });
    throw new Error(message);
  }
  if (!options?.allowPastDue) {
    assertBillingWriteAllowed(useUserStore.getState().billingStatus);
  }
  return withAction(label, action, {
    suppressOverlay: options?.suppressOverlay,
  });
}

export function isPaymentRequiredError(error: any): boolean {
  return (
    error instanceof PaymentRequiredError ||
    error?.name === "PaymentRequiredError"
  );
}

export function presentActionError(
  error: any,
  present: (message: string) => void,
  fallback = "Request failed",
): void {
  if (isPaymentRequiredError(error)) return;
  present(error?.message || fallback);
}

export function taskNotesPlaceholder(type?: string) {
  const key = (type || "").toLowerCase();
  return (
    TASK_NOTES_PLACEHOLDER_BY_TYPE[key] ||
    TASK_NOTES_PLACEHOLDER_BY_TYPE.other ||
    "Example: Describe the issue and what needs to be done."
  );
}

export function sortTasksOpenFirst(tasks: Task[]) {
  return [...tasks].sort((a, b) => {
    const aOpen = a.status !== "done";
    const bOpen = b.status !== "done";
    if (aOpen !== bOpen) return aOpen ? -1 : 1;
    const aCreated = new Date(a.createdAt || 0).getTime();
    const bCreated = new Date(b.createdAt || 0).getTime();
    return bCreated - aCreated;
  });
}

const isTaskOpenStatus = (status?: Task["status"]) =>
  status === "pending" || status === "unassigned";

const scheduledForTime = (value?: string | null) => {
  if (!value) return Number.NaN;
  const dayKey = value.split("T")[0] || value;
  const parsed = Date.parse(`${dayKey}T00:00:00`);
  return Number.isNaN(parsed) ? Number.NaN : parsed;
};

export function sortTasksForTable(tasks: Task[]) {
  return [...tasks].sort((a, b) => {
    const aOpen = isTaskOpenStatus(a.status);
    const bOpen = isTaskOpenStatus(b.status);
    if (aOpen !== bOpen) return aOpen ? -1 : 1;

    const aScheduled = scheduledForTime(a.scheduledFor);
    const bScheduled = scheduledForTime(b.scheduledFor);
    if (
      !Number.isNaN(aScheduled) &&
      !Number.isNaN(bScheduled) &&
      aScheduled !== bScheduled
    ) {
      return aOpen ? aScheduled - bScheduled : bScheduled - aScheduled;
    }
    if (!Number.isNaN(aScheduled) && Number.isNaN(bScheduled)) return -1;
    if (Number.isNaN(aScheduled) && !Number.isNaN(bScheduled)) return 1;

    const aCreated = new Date(a.createdAt || 0).getTime();
    const bCreated = new Date(b.createdAt || 0).getTime();
    return bCreated - aCreated;
  });
}

export function filterTasksByMachineId(tasks: Task[], machineId: string) {
  return tasks.filter((t) => !!t.machineId && t.machineId === machineId);
}

export function filterTasksByLocationId(tasks: Task[], locationId: string) {
  return tasks.filter((t) => !!t.locationId && t.locationId === locationId);
}

// Modal helpers
export function useEscClose(enabled: boolean, onClose: () => void) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, onClose]);
}

export function useClickOutside(
  enabled: boolean,
  ref: React.RefObject<HTMLElement | null>,
  onClose: () => void,
) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [enabled, onClose, ref]);
}

// ////// Date Utils ////// //
export function todayLocalISODate() {
  return toLocalISODate(new Date());
}

export function localDateLabel(date: Date = new Date()) {
  return date.toLocaleDateString(undefined, {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDate(value?: string | null, options?: Intl.DateTimeFormatOptions): string {
  if (!value) return "—";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? isoDateToLocalDate(value)
    : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return options ? d.toLocaleDateString(undefined, options) : localDateLabel(d);
}

export function formatTimezoneLabel(
  timezone?: string | null,
  at: Date = new Date(),
): string {
  if (!timezone) return "—";

  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      timeZoneName: "short",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const abbreviation =
      formatter.formatToParts(at).find((part) => part.type === "timeZoneName")
        ?.value || "";

    const usZoneLabels: Record<
      string,
      { standard: string; daylight: string; generic: string }
    > = {
      "America/New_York": {
        standard: "Eastern Standard Time",
        daylight: "Eastern Daylight Time",
        generic: "Eastern Time",
      },
      "America/Chicago": {
        standard: "Central Standard Time",
        daylight: "Central Daylight Time",
        generic: "Central Time",
      },
      "America/Denver": {
        standard: "Mountain Standard Time",
        daylight: "Mountain Daylight Time",
        generic: "Mountain Time",
      },
      "America/Los_Angeles": {
        standard: "Pacific Standard Time",
        daylight: "Pacific Daylight Time",
        generic: "Pacific Time",
      },
      "America/Phoenix": {
        standard: "Mountain Standard Time",
        daylight: "Mountain Standard Time",
        generic: "Mountain Time",
      },
    };

    const zone = usZoneLabels[timezone];
    if (!zone) {
      return abbreviation ? `${timezone} (${abbreviation})` : timezone;
    }

    const upperAbbr = abbreviation.toUpperCase();
    const label = upperAbbr.endsWith("DT")
      ? zone.daylight
      : upperAbbr.endsWith("ST")
        ? zone.standard
        : zone.generic;

    return abbreviation ? `${label} (${abbreviation})` : label;
  } catch {
    return timezone;
  }
}

export function formatDateShort(value?: string | null): string {
  if (!value) return "—";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? isoDateToLocalDate(value)
    : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function formatDateShortNoYear(value?: string | null): string {
  if (!value) return "—";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? isoDateToLocalDate(value)
    : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function formatDateWeekdayShort(value?: string | null): string {
  if (!value) return "—";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? isoDateToLocalDate(value)
    : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
  });
}

export function formatDateWeekdayShortAbbrev(value?: string | null): string {
  if (!value) return "—";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? isoDateToLocalDate(value)
    : new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function formatNextVisitLabels(value?: Date | string | null): {
  long: string;
  short: string;
} {
  if (!value) return { long: "—", short: "—" };
  const d =
    value instanceof Date
      ? value
      : /^\d{4}-\d{2}-\d{2}$/.test(value)
        ? isoDateToLocalDate(value)
        : new Date(value);
  if (Number.isNaN(d.getTime())) return { long: "—", short: "—" };
  const today = new Date();
  const todayMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const targetMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round(
    (targetMidnight.getTime() - todayMidnight.getTime()) / 86_400_000,
  );
  if (diffDays === 0) return { long: "Today", short: "Today" };
  if (diffDays === 1) return { long: "Tomorrow", short: "Tomorrow" };
  const localIso = toLocalISODate(d);
  return {
    long: formatDateWeekdayShort(localIso),
    short: formatDateWeekdayShortAbbrev(localIso),
  };
}

export function formatDateNumericVariants(value?: string | null): string {
  if (!value) return "";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? isoDateToLocalDate(value)
    : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const year = d.getFullYear();
  const mm = String(month).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  const yy = String(year).slice(-2);
  return [
    `${month}/${day}/${year}`,
    `${mm}/${dd}/${year}`,
    `${month}/${day}/${yy}`,
    `${mm}/${dd}/${yy}`,
  ].join(" ");
}

export function formatDateSearchText(value?: string | null): string {
  if (!value) return "";
  const d = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? isoDateToLocalDate(value)
    : new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  const iso = toLocalISODate(d);
  return [
    iso,
    formatDateNumericVariants(iso),
    d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }),
    d.toLocaleDateString("en-US", { month: "long", day: "numeric" }),
    d.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    }),
  ].join(" ");
}

export function formatDateTime(value?: string | null, format: "dateTime" | "time" = "dateTime", includeSeconds = false): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const time = d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    ...(includeSeconds ? { second: "2-digit" as const } : {}),
    hour12: true,
  });
  if (format === "time") return time;
  const date = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return `${time} ${date}`;
}

export function formatCompactDateTime(value: string): string {
  return new Date(value).toLocaleString(undefined, {
    year: "2-digit",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

export function formatDateRangeShort(
  start?: string | null,
  end?: string | null,
  timeZone?: string,
): string {
  if (!start || !end) return "—";
  const startDate = /^\d{4}-\d{2}-\d{2}$/.test(start)
    ? isoDateToLocalDate(start)
    : new Date(start);
  const endDate = /^\d{4}-\d{2}-\d{2}$/.test(end)
    ? isoDateToLocalDate(end)
    : new Date(end);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
    return "—";
  }
  const shortFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone,
    month: "short",
    day: "numeric",
  });
  const endFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone,
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const yearFormatter = new Intl.DateTimeFormat(undefined, {
    timeZone,
    year: "numeric",
  });
  const startText = shortFormatter.format(startDate);
  const endText = endFormatter.format(endDate);
  const startYear = yearFormatter.format(startDate);
  const endYear = yearFormatter.format(endDate);
  if (startYear !== endYear) {
    const startWithYear = new Intl.DateTimeFormat(undefined, {
      timeZone,
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(startDate);
    return `${startWithYear} – ${endText}`;
  }
  return `${startText} – ${endText}`;
}

export function formatDay(dateIso: string) {
  const d = isoDateToLocalDate(dateIso);
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

export function formatShortDate(dateIso: string) {
  const d = isoDateToLocalDate(dateIso);
  return d.toLocaleDateString(undefined, {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
}

export function toLocalISODate(d: Date = new Date()): string {
  const year = d.getFullYear();
  const month = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Local wall time for minute-precision date/time form controls. */
export function toLocalISODateTime(date: Date = new Date()): string {
  return `${toLocalISODate(date)}T${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

export function daysSince(value?: string | null): number | null {
  if (!value) return null;
  const start = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? isoDateToLocalDate(value)
    : new Date(value);
  if (Number.isNaN(start.getTime())) return null;
  const startKey = toLocalISODate(start);
  const todayKey = toLocalISODate(new Date());
  const startDate = isoDateToLocalDate(startKey);
  const todayDate = isoDateToLocalDate(todayKey);
  const diff = todayDate.getTime() - startDate.getTime();
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
}

export function formatAgeSince(value?: string | null): string {
  const days = daysSince(value);
  if (days === null) return "—";
  if (days < 30) return `${days} ${days === 1 ? "day" : "days"}`;
  if (days < 730) {
    const months = Math.floor(days / 30);
    return `${months} ${months === 1 ? "month" : "months"}`;
  }
  const years = Math.floor(days / 365);
  return `${years} years`;
}

export function daysBetween(
  startValue?: string | null,
  endValue?: string | null,
): number | null {
  if (!startValue || !endValue) return null;
  const start = /^\d{4}-\d{2}-\d{2}$/.test(startValue)
    ? isoDateToLocalDate(startValue)
    : new Date(startValue);
  const end = /^\d{4}-\d{2}-\d{2}$/.test(endValue)
    ? isoDateToLocalDate(endValue)
    : new Date(endValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const startKey = toLocalISODate(start);
  const endKey = toLocalISODate(end);
  const startDate = isoDateToLocalDate(startKey);
  const endDate = isoDateToLocalDate(endKey);
  const diff = endDate.getTime() - startDate.getTime();
  return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
}

export function formatDaysAndHours(seconds?: number | null): string {
  if (seconds === undefined || seconds === null || seconds < 0) return "—";
  const totalHours = Math.floor(seconds / 3_600);
  if (totalHours < 1) return "Less than 1 hour";
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const parts: string[] = [];
  if (days) parts.push(`${days} ${days === 1 ? "day" : "days"}`);
  if (hours) parts.push(`${hours} ${hours === 1 ? "hour" : "hours"}`);
  return parts.join(", ");
}

export function isoDateToLocalDate(dateIso: string): Date {
  const parts = dateIso.split("-").map(Number);
  if (parts.length === 3) {
    return new Date(parts[0], (parts[1] || 1) - 1, parts[2] || 1);
  }
  const fallback = new Date(dateIso);
  return Number.isNaN(fallback.getTime()) ? new Date() : fallback;
}

export function formatDistance(miles?: number | null) {
  if (miles === undefined || miles === null || Number.isNaN(miles)) return "—";
  return `${Math.max(0, miles).toLocaleString(undefined, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} mi`;
}

export function formatCurrencySpaced(value: number, decimals = 2) {
  const parts = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: decimals,
  }).formatToParts(value);
  return parts
    .map((part) => (part.type === "currency" ? `${part.value} ` : part.value))
    .join("");
}

export function easeInOutQuad(value: number) {
  return value < 0.5 ? 2 * value * value : 1 - Math.pow(-2 * value + 2, 2) / 2;
}

export function formatTimeAgo(value?: string | number | null, style: "short" | "long" | "calendar" = "short") {
  if (value === null || value === undefined) return "—";
  const seconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (!Number.isFinite(seconds) || seconds < 0) return "—";
  if (style === "calendar") {
    const now = new Date();
    const date = new Date(value);
    // Compare local calendar days in UTC so daylight saving changes do not skew the age.
    const day = (date: Date) => Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    const days = Math.round((day(now) - day(date)) / 86400000);
    if (days === 0) return "Today";
    const [unit, size]: [Intl.RelativeTimeFormatUnit, number] =
      days >= 365 ? ["year", 365] : days >= 30 ? ["month", 30] : days >= 7 ? ["week", 7] : ["day", 1];
    return new Intl.RelativeTimeFormat(undefined, { numeric: "always" }).format(-Math.floor(days / size), unit);
  }
  if (seconds < 5) return "just now";
  const units: [Intl.RelativeTimeFormatUnit, number, string][] = [
    ["year", 31536000, "y"], ["month", 2592000, "mo"], ["week", 604800, "w"],
    ["day", 86400, "d"], ["hour", 3600, "h"], ["minute", 60, "m"], ["second", 1, "s"],
  ];
  const [unit, size, suffix] = units.find(([, size]) => seconds >= size)!;
  const amount = Math.floor(seconds / size);
  return style === "long"
    ? new Intl.RelativeTimeFormat(undefined, { numeric: "always" }).format(-amount, unit)
    : `${amount}${suffix} ago`;
}

export function taskAssigneeLabel(
  task: {
    userId?: string | null;
    assigneeName?: string | null;
    assigneeEmail?: string | null;
  },
  resolveUser?: (userId: string) => UserLabelInput | null | undefined,
): string {
  if (!task.userId) return task.assigneeName || "Unassigned";
  const resolved = resolveUser ? resolveUser(task.userId) : null;
  const resolvedLabel = resolved ? formatUserLabel(resolved) : "—";
  if (resolvedLabel && resolvedLabel !== "—") return resolvedLabel;
  if (task.assigneeName && task.assigneeEmail) {
    return `${task.assigneeName} (${task.assigneeEmail})`;
  }
  return task.assigneeName || task.assigneeEmail || "—";
}

export function formatDuration(minutes?: number | null) {
  if (minutes === undefined || minutes === null || Number.isNaN(minutes))
    return "—";
  const total = Math.max(0, Math.round(minutes));
  const hours = Math.floor(total / 60);
  const mins = total % 60;
  if (hours === 0) return `${mins}m`;
  return `${hours}h ${mins}m`;
}

export function daysFromNow(dateIso: string) {
  const today = new Date();
  const target = new Date(dateIso);
  const diff = Math.ceil(
    (target.getTime() - new Date(today.toDateString()).getTime()) /
      (1000 * 60 * 60 * 24),
  );
  if (diff === 0) return "today";
  if (diff === 1) return "1 day";
  if (diff < 0) return `${Math.abs(diff)} day${diff < -1 ? "s" : ""} ago`;
  return `${diff} days`;
}

export function displayAmount(
  val: number | undefined,
  formatter: (n: number) => string,
) {
  return val && Math.abs(val) > 0 ? formatter(val) : "";
}

export function providerLabel(provider?: string) {
  if (!provider) return "";
  const p = provider.toLowerCase();
  if (p === "cpi") return "CPI";
  return p.charAt(0).toUpperCase() + p.slice(1);
}

// Persist tab selection in the URL (?tab=...) so reloads stay on the same tab.
export function usePersistentTab<T extends string>(
  tabKeys: T[],
  defaultTab: T,
  paramKey = "tab",
  replace = false,
): [T, (t: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams();

  const resolveTab = useCallback((): T => {
    const t = searchParams.get(paramKey) || "";
    return (tabKeys.includes(t as T) ? t : defaultTab) as T;
  }, [searchParams, tabKeys, defaultTab, paramKey]);

  const [tab, setTabState] = useState<T>(resolveTab);

  // keep local state in sync with URL changes
  useEffect(() => {
    const next = resolveTab();
    setTabState((prev) => (prev === next ? prev : next));
  }, [resolveTab]);

  const setTab = (t: T) => {
    setTabState(t);
    const next = new URLSearchParams(searchParams);
    next.set(paramKey, t);
    setSearchParams(next, { replace });
  };

  return [tab, setTab];
}

export function buildLocationLookup(
  locations: ({ id?: string; name?: string } | null | undefined)[],
): Record<string, string> {
  const map: Record<string, string> = {};
  locations.forEach((loc) => {
    if (loc && loc.id) map[loc.id] = loc.name || loc.id;
  });
  return map;
}

export function buildLocationAddressLookup(
  locations: (
    | {
        id?: string;
        address?: {
          name?: string;
          street?: string;
          city?: string;
          state?: string;
          zip?: string;
          country?: string;
        };
      }
    | null
    | undefined
  )[],
): Record<string, string> {
  const map: Record<string, string> = {};
  locations.forEach((loc) => {
    if (loc && loc.id) map[loc.id] = formatAddress(loc.address) || "";
  });
  return map;
}

export function getLocationId(loc?: string | { id?: string } | null) {
  if (!loc) return undefined;
  return typeof loc === "string" ? loc : loc.id;
}

export function isMachineLocationMismatch(
  machine: { location?: string | { id?: string } | null } | undefined,
  selectedLocationId: string | undefined,
): boolean {
  const machineLocationId = getLocationId(machine?.location || null);
  return (
    !!machineLocationId &&
    !!selectedLocationId &&
    machineLocationId !== selectedLocationId
  );
}

export function getLocationName(
  loc: string | { id?: string; name?: string } | undefined,
  lookup: Record<string, string>,
) {
  if (!loc) return "";
  if (typeof loc === "string") return lookup[loc] || loc;
  return loc.name || (loc.id ? lookup[loc.id] : "") || "";
}

export function getDefaultAssigneeId(
  locationId: string | undefined,
  locations: { id?: string; defaultAssigneeId?: string | null }[],
): string {
  if (!locationId) return "";
  const match = locations.find((loc) => loc?.id === locationId);
  return match?.defaultAssigneeId || "";
}

type DynamicSchedulingInput = {
  revenueThreshold?: number;
  cashThreshold?: number;
  cardThreshold?: number;
  inventoryTrigger?: number;
  revenueSchedule?: "same_day" | "tomorrow" | "in_2_days";
};

export function formatDynamicSchedulingSummary({
  revenueThreshold,
  cashThreshold,
  cardThreshold,
  inventoryTrigger,
  revenueSchedule = "same_day",
}: DynamicSchedulingInput): { primary: string; secondary: string } | null {
  const parts: string[] = [];
  if (
    typeof revenueThreshold === "number" &&
    Number.isFinite(revenueThreshold) &&
    revenueThreshold > 0
  ) {
    parts.push(`makes $${revenueThreshold} in total revenue`);
  }
  for (const [value, label] of [[cashThreshold, "cash"], [cardThreshold, "card sales"]] as const) {
    if (typeof value === "number" && Number.isFinite(value) && value > 0) {
      parts.push(`${parts.length ? "" : "makes "}$${value} in ${label}`);
    }
  }
  if (
    typeof inventoryTrigger === "number" &&
    Number.isFinite(inventoryTrigger)
  ) {
    parts.push(`falls to ${inventoryTrigger}% inventory`);
  }
  if (!parts.length) return null;
  const joined =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(" or ")} or ${parts.slice(-1)}`;
  const scheduleLabel =
    revenueSchedule === "tomorrow"
      ? "the next day"
      : revenueSchedule === "in_2_days"
        ? "in 2 days"
        : "the same day";
  const primary = `Once this location ${joined}, it will be queued for ${scheduleLabel}.`;
  const secondary = "You can always update this later.";
  return { primary, secondary };
}

export function sortDaysByWeek(days: string[]): string[] {
  const seen = new Set<string>();
  const ordered: string[] = [];
  DAYS.forEach((day) => {
    if (days.includes(day) && !seen.has(day)) {
      ordered.push(day);
      seen.add(day);
    }
  });
  days.forEach((day) => {
    if (!seen.has(day)) {
      ordered.push(day);
      seen.add(day);
    }
  });
  return ordered;
}

export function formatPaymentForms(machine: Machine): string {
  const forms: string[] = [];
  if (machine.cash) forms.push("Cash");
  if (machine.coin) forms.push("Coin");
  if (machine.card) forms.push("Card");
  return forms.join(", ");
}

export function hasPaymentMethod(machine?: Machine | null): boolean {
  if (!machine) return false;
  return Boolean(machine.cash || machine.coin || machine.card);
}

export function filterMachinesWithPaymentMethods(
  machines: Machine[] = [],
): Machine[] {
  return machines.filter((machine) => hasPaymentMethod(machine));
}

export type HoursLine = {
  day: string;
  value: string;
  open: string;
  close: string;
  closed: boolean;
};

export function formatHoursLines(
  hours?: Record<string, { open?: string; close?: string; closed?: boolean }>,
  editMode?: boolean,
): HoursLine[] {
  const providedDays = hours ? Object.keys(hours) : [];
  const orderedKeys = [
    ...DAYS,
    ...providedDays.filter((d) => !DAYS.includes(d)),
  ];

  return orderedKeys.map((day) => {
    const entry = hours?.[day];
    if (!entry || entry.closed) {
      return {
        day,
        value: editMode ? "" : "Closed",
        open: "",
        close: "",
        closed: true,
      };
    }
    const openRaw = entry.open ?? "00:00";
    const closeRaw = entry.close ?? "00:00";
    const open = to12hString(openRaw);
    const close = to12hString(closeRaw);
    return {
      day,
      value: `${open} - ${close}`,
      open: openRaw,
      close: closeRaw,
      closed: false,
    };
  });
}

export function to12hString(time: string) {
  const { hour, minute, period } = from24h(time);
  const mm = minute.toString().padStart(2, "0");
  return `${hour}:${mm} ${period}`;
}

export function formatFullName(first?: string | null, last?: string | null) {
  return [first, last].filter(Boolean).join(" ").trim();
}

export function formatRoleLabel(role?: string | null) {
  const val = (role || "").trim();
  if (!val) return "Owner";
  return val.charAt(0).toUpperCase() + val.slice(1);
}

export function formatAddress(address?: {
  name?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}) {
  if (!address) return "";
  const parts = [
    address.street,
    [address.city, address.state].filter(Boolean).join(", "),
    address.zip,
    address.country,
  ]
    .filter((p) => (p || "").toString().trim().length)
    .map((p) => (p || "").toString().trim());
  return parts.join(" • ");
}

export function formatAddressWithCommas(address?: {
  name?: string;
  street?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
}) {
  if (!address) return "";
  return [
    address.street,
    address.city,
    address.state,
    address.zip,
    address.country,
  ]
    .filter((part) => (part || "").toString().trim().length)
    .map((part) => (part || "").toString().trim())
    .join(", ");
}

export function addressesEqual(
  a?: {
    name?: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  } | null,
  b?: {
    name?: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  } | null,
) {
  const normalize = (addr?: {
    name?: string;
    street?: string;
    city?: string;
    state?: string;
    zip?: string | number;
    country?: string;
  }) => ({
    name: (addr?.name || "").trim(),
    street: (addr?.street || "").trim(),
    city: (addr?.city || "").trim(),
    state: (addr?.state || "").trim(),
    zip: addr?.zip ? String(addr.zip).trim() : "",
    country: (addr?.country || "").trim(),
  });
  return (
    JSON.stringify(normalize(a || undefined)) ===
    JSON.stringify(normalize(b || undefined))
  );
}

// Machine type helpers
export const machineTypeKey = (label?: string): string | undefined => {
  if (!label) return undefined;
  return label
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
};

export const machineTypeLabel = (
  key?: string,
  allTypes: string[] = MACHINE_TYPES,
): string | undefined => {
  if (!key) return undefined;
  const normalizedKey = machineTypeKey(key);
  const match = allTypes.find((t) => machineTypeKey(t) === normalizedKey);
  return match || key;
};

export const EMPTY_ADDRESS = {
  name: "",
  street: "",
  city: "",
  state: "",
  zip: "",
  country: "",
};

// loosely parse "9", "9am", "9:30", "9:30pm" into 24h HH:mm
export function to24hInput(input: string) {
  const match = input.trim().match(/^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)?$/i);
  if (!match) return input;
  let hour = parseInt(match[1], 10);
  const minute = match[2] ? parseInt(match[2], 10) : 0;
  const period = (match[3] || "").toUpperCase();
  if (period === "PM" && hour < 12) hour += 12;
  if (period === "AM" && hour === 12) hour = 0;
  return `${hour.toString().padStart(2, "0")}:${minute
    .toString()
    .padStart(2, "0")}`;
}

// Hook to close dialogs on Escape key
export function useEscapeToClose(enabled: boolean, onClose: () => void) {
  useEffect(() => {
    if (!enabled) return;
    const handler = (e: KeyboardEvent) => {
      if (document.body?.dataset?.calendarOpen === "true") return;
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled, onClose]);
}

// Require a second Escape (or clicking close) to close; first Escape highlights close button.
export function useEscapeToConfirmClose(enabled: boolean, onClose: () => void, listen = true) {
  const [confirming, setConfirming] = useState(false);
  const confirmClose = useCallback(() => {
    if (!enabled || document.body?.dataset?.calendarOpen === "true") return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    onClose();
  }, [confirming, enabled, onClose]);

  useEffect(() => {
    if (!enabled) setConfirming(false);
    if (!enabled || !listen) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !event.defaultPrevented && !event.repeat) confirmClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [confirmClose, enabled, listen]);

  useEffect(() => {
    if (!enabled || !confirming) return;
    const clear = () => setConfirming(false);
    document.addEventListener("mousedown", clear);
    document.addEventListener("touchstart", clear);
    return () => {
      document.removeEventListener("mousedown", clear);
      document.removeEventListener("touchstart", clear);
    };
  }, [confirming, enabled]);

  const requestClose = () => {
    setConfirming(false);
    onClose();
  };

  return { confirming, requestClose, confirmClose };
}

// Masks
export function mask(key?: string | null, visibleChars = 5): string {
  if (!key) return "—";
  const visible = key.slice(0, visibleChars);
  const hidden =
    key.length > visibleChars ? "*".repeat(key.length - visibleChars) : "";
  return `${visible}${hidden}`;
}

export function filterByQuery<T>(
  items: T[],
  query: string,
  fields: Array<(item: T) => string | number | null | undefined>,
): T[] {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return items;
  return items.filter((item) =>
    fields.some((getter) => {
      const raw = getter(item);
      if (raw === null || raw === undefined) return false;
      return String(raw).toLowerCase().includes(normalized);
    }),
  );
}

export function sortByLabel<T extends { label: string }>(items: T[]): T[] {
  if (!items.length) return items;
  return [...items].sort((a, b) => a.label.localeCompare(b.label));
}

export function sortByNameIfPresent<T>(
  items: T[],
  getName?: (item: T) => string | null | undefined,
): T[] {
  if (!items.length) return items;
  const nameGetter =
    getName ??
    ((item: any) => (item && "name" in item ? item.name : undefined));
  const firstName = nameGetter(items[0]);
  if (firstName === undefined || firstName === null) return items;
  return [...items].sort((a, b) => {
    const aName = (nameGetter(a) || "").toString();
    const bName = (nameGetter(b) || "").toString();
    return aName.localeCompare(bName);
  });
}

export type DateOnlyFormat = "short" | "weekday";

/**
 * Formats a YYYY-MM-DD calendar date without allowing UTC conversion to shift
 * it into the previous or following day in the viewer's timezone.
 */
export function formatDateOnly(
  value: string,
  format: DateOnlyFormat = "short",
): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return value;

  const date = new Date(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
  );
  return date.toLocaleDateString(undefined, {
    ...(format === "weekday" ? { weekday: "long" as const } : {}),
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

export { formatCents } from "./currency";
